import { map, mergeMap, switchMap, delay, takeUntil } from 'rxjs/operators'
import { combineEpics } from 'redux-observable'
import { Epic, EpicRefined } from 'interfaces/middleware'
import {
  GET_FUTURE_URL,
  FUTURE_LAYER_URL,
  CREATE_FUTURE_LAYER_URL,
  ASSUMPTIONS_URL,
  POST_LAYER_CHECK_URL,
  RESET_ASSUMPTIONS_URL,
  LAYER_HINTS_URL,
} from 'interfaces/ajax'
import {
  GET_FUTURE,
  getFutureActionCreator,
  PATCH_FUTURE_LAYER,
  patchFutureLayerActionCreator,
  POST_FUTURE_LAYER,
  postFutureLayerActionCreator,
  DELETE_FUTURE_LAYER,
  deleteFutureLayerActionCreator,
  GET_ASSUMPTIONS,
  getAssumptionsActionCreator,
  PATCH_ASSUMPTIONS,
  patchAssumptionsActionCreator,
  LAYER_CHECK,
  layerCheckActionCreator,
  RESET_ASSUMPTIONS,
  resetAssumptionsActionCreator,
  FutureActions,
  DropLayerCheckAction,
  GET_LAYER_HINTS,
  DropGetLayerHintsAction,
  getLayerHintsActionCreator,
} from 'components/features/future/actions'
import { Observable, of, empty } from 'rxjs'
import { cleanUpObj, normalizeFutureResponse } from 'components/features/future/helpers'
import { layerSelector } from 'components/features/future/selectors'
import { captureWarning } from 'helpers/errorHelpers'
import { killAllFloatingPanesActionCreator, modifyFloatingPaneActionCreator, openSubscriptionPageActionCreator } from 'actions/floatingPanesActions'
import { floatingPanesCollectionSelector } from 'selectors/floatingPanesSelectors'
import { differenceProps } from 'helpers/formHelpers'
import { ACTIVATE_ANCHOR, activateAnchorActionCreator } from 'actions/navigationActions'
import { Action } from 'actions'
import { addNotificationActionCreator } from 'actions/notificationsActions'
import { generate } from 'randomstring'
import { resolveErrorById } from 'constants/errors'
import { byType } from 'rxOperators/byType'
import { catchAjaxSafe } from 'rxOperators/catchSafe'
import { AjaxError } from 'rxjs/ajax'
import { getItemFromCollection } from 'helpers/normalizeHelpers'
import { filterRequest, filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { ajaxCatch, AjaxCatchExtraActions } from 'rxOperators/ajaxCatch'


const getFutureEpic: Epic = (action$, _, { get }) =>
  action$
    .pipe(
      filterRequestOfType(GET_FUTURE, 'request'),
      mergeMap(({ payload }) =>
        get(GET_FUTURE_URL).pipe(
          mergeMap(({ response }) => {
            return of(getFutureActionCreator.success(payload.params, normalizeFutureResponse(response)))
          }),
          catchAjaxSafe((error) => of(getFutureActionCreator.fail(payload.params, error.status)))
        )
      )
    )

const handlePremiumOnlyResponse = <I>(handler: (error: AjaxError) => Action[]) => (stream: Observable<I>): Observable<Action> => {
  return stream.pipe(
    catchAjaxSafe((error) => {
      const actions: Action[] = [
        ...handler(error),
      ]
      if (error.status === 402) {
        actions.push(openSubscriptionPageActionCreator() as Action)
      }

      return of(...actions)
    })
  )
}

const patchLayerEpic: Epic = (action$, store, { patch }) =>
  action$.pipe(
    filterRequestOfType(PATCH_FUTURE_LAYER, 'request'),
    mergeMap(({ payload }) => {
      const { params } = payload
      const state = store.getState()
      const layer = layerSelector(state, params)

      if (!layer) {
        captureWarning('Layer not found, PATCH', { layer, params })
        return empty()
      }

      const diffProps = differenceProps(layer as any, params as any)
      const url = {
        url: FUTURE_LAYER_URL,
        params: {
          uid: params.uid,
          slug: layer.slug,
        },
      }

      return patch(url, { body: diffProps }).pipe(
        mergeMap(({ response }) => {
          return of(patchFutureLayerActionCreator.success(payload.params, normalizeFutureResponse(response)))
        }))
        .pipe(
          handlePremiumOnlyResponse((error) => {
            const resultActions: Action[] = [
              patchFutureLayerActionCreator.error(payload.params, error.status, error.xhr.response),
            ]

            const hasShownPanes = state.app.floatingPanes.panes.allKeys.length !== 0
            const { response } = error.xhr
            if (!hasShownPanes && response.non_field_errors && response.non_field_errors.length) {
              resultActions.push(
                addNotificationActionCreator({
                  id: generate(),
                  type: 'text-icon',
                  text: resolveErrorById(response.non_field_errors[0]),
                  icon: 'fail',
                  showMode: 'fresh',
                })
              )
            }

            return resultActions
          })
        )
    }))

const postLayerEpic: Epic = (action$, _, { post }) =>
  action$.pipe(
    filterRequestOfType(POST_FUTURE_LAYER, 'request'),
    mergeMap(({ payload }) => {
      const { params } = payload

      const url = {
        url: CREATE_FUTURE_LAYER_URL,
        params: {
          slug: params.slug,
        },
      }

      return post(url, { body: params }).pipe(
        mergeMap(({ response }) => {
          return of(postFutureLayerActionCreator.success(payload.params, normalizeFutureResponse(response)))
        }))
        .pipe(
          handlePremiumOnlyResponse((error) => [postFutureLayerActionCreator.error(payload.params, error.status, error.xhr.response)])
        )
    }))

const postLayerSuccessEpic: Epic = (action$) =>
  action$.pipe(
    filterRequestOfType(POST_FUTURE_LAYER, 'success'),
    map(killAllFloatingPanesActionCreator)
  )

const patchLayerSuccessEpic: Epic = (action$, store) =>
  action$.pipe(
    filterRequestOfType(PATCH_FUTURE_LAYER, 'success'),
    mergeMap(() => {
      const state = store.getState()
      const { allKeys, byKey } = floatingPanesCollectionSelector(state)
      const key = allKeys[allKeys.length - 1]
      const pane = byKey[key]
      if (pane && pane.type === 'future' && pane.mode === 'layer-details') {
        return of(killAllFloatingPanesActionCreator())
      }
      return empty()
    }))

const deleteLayerEpic: Epic = (action$, store, { deleteAjax }) =>
  action$
    .pipe(
      filterRequestOfType(DELETE_FUTURE_LAYER, 'request'),
      mergeMap(({ payload }) => {
        const { params } = payload
        const { uid } = params
        const state = store.getState()
        const layer = getItemFromCollection(state.data.future.layers, uid)
        if (!layer) {
          return empty()
        }

        const url = {
          url: FUTURE_LAYER_URL,
          params: {
            uid: params.uid,
            slug: layer.slug,
          },
        }

        return deleteAjax(url).pipe(
          mergeMap(({ response }) => {
            return of(deleteFutureLayerActionCreator.success(payload.params, normalizeFutureResponse(response)))
          }))
          .pipe(
            catchAjaxSafe((error) => of(deleteFutureLayerActionCreator.error(payload.params, error.status, error.xhr.response)))
          )
      }))

const deleteLayerSuccessEpic: Epic = (action$, store) =>
  action$.pipe(
    filterRequestOfType(DELETE_FUTURE_LAYER, 'success'),
    mergeMap(() => {
      const state = store.getState()
      const { allKeys, byKey } = floatingPanesCollectionSelector(state)
      const key = allKeys[allKeys.length - 1]
      const pane = byKey[key]
      if (pane && pane.type === 'future' && pane.mode === 'layer-details') {
        return of(killAllFloatingPanesActionCreator())
      }
      return empty()
    }))

const getAssumptionsEpic: Epic = (action$, _, { get }) =>
  action$.pipe(
    filterRequestOfType(GET_ASSUMPTIONS, 'request'),
    mergeMap(({ payload }) => {
      return get(ASSUMPTIONS_URL).pipe(
        mergeMap(({ response }) => {
          return of(getAssumptionsActionCreator.success(payload.params, response))
        }))
        .pipe(
          catchAjaxSafe((error) => of(getAssumptionsActionCreator.fail(payload.params, error.status)))
        )
    }))

const patchAssumptionsEpic: Epic = (action$, store, { patch }) =>
  action$.pipe(
    filterRequestOfType(PATCH_ASSUMPTIONS, 'request'),
    mergeMap(({ payload }) => {
      const { params } = payload
      const state = store.getState()
      const assumptions = state.data.future.assumptions.data || {}
      const diffProps = differenceProps(assumptions, params)

      return patch(ASSUMPTIONS_URL, { body: diffProps }).pipe(
        mergeMap(({ response }) => {
          return of(patchAssumptionsActionCreator.success(payload.params, normalizeFutureResponse(response)))
        }))
        .pipe(
          handlePremiumOnlyResponse((error) => [patchAssumptionsActionCreator.error(payload.params, error.status, error.xhr.response)])
        )
    }))

const patchAssumptionsSuccessEpic: Epic = (action$) =>
  action$.pipe(
    filterRequestOfType(PATCH_ASSUMPTIONS, 'success'),
    map(killAllFloatingPanesActionCreator)
  )

const useHintsEpic: EpicRefined<AjaxCatchExtraActions | FutureActions> = (action$, _, { post }) =>
  action$.pipe(
    filterRequest(GET_LAYER_HINTS),
    switchMap((action) => of(action).pipe(
      // delay(500),
      takeUntil(action$.pipe(
        byType(DropGetLayerHintsAction.type)
      )),
      mergeMap(({ payload }) => {
        const { params } = payload

        const request = {
          url: LAYER_HINTS_URL,
          params: {
            slug: params.slug,
          },
        }
        const body = cleanUpObj(params)
        return post(request, { body }).pipe(
          takeUntil(action$.pipe(
            byType(DropGetLayerHintsAction.type)
          )),
          mergeMap((result) => {
            const { response } = result
            console.log('result')
            return of(getLayerHintsActionCreator.success(params, response))
          }))
          .pipe(
            ajaxCatch((error) => of(getLayerHintsActionCreator.error(params, error.status, error.xhr.response)))
          )
      }),
    )
    ))

const layerCheckEpic: EpicRefined<AjaxCatchExtraActions | FutureActions> = (action$, _, { post }) =>
  action$.pipe(
    filterRequest(LAYER_CHECK),
    switchMap((action) => of(action).pipe(
      delay(500),
      takeUntil(action$.pipe(
        byType(DropLayerCheckAction.type)
      )),
      mergeMap(({ payload }) => {
        const { params } = payload

        const request = {
          url: POST_LAYER_CHECK_URL,
          params: {
            slug: params.slug,
          } as any,
        }

        return post(request, { body: params }).pipe(
          takeUntil(action$.pipe(
            byType(DropLayerCheckAction.type)
          )),
          mergeMap(({ response }) => {
            return of(layerCheckActionCreator.success(payload.params, response))
          }))
          .pipe(
            ajaxCatch((error) => of(layerCheckActionCreator.error(payload.params, error.status, error.xhr.response)))
          )
      }),
    )
    ))

const resetAssumptionsEpic: Epic = (action$, _, { post }) =>
  action$.pipe(
    filterRequestOfType(RESET_ASSUMPTIONS, 'request'),
    mergeMap(({ payload }) =>
      post(RESET_ASSUMPTIONS_URL).pipe(
        mergeMap(({ response }) => {
          return of(resetAssumptionsActionCreator.success(payload.params, normalizeFutureResponse(response)))
        }))
        .pipe(
          catchAjaxSafe((error) => of(resetAssumptionsActionCreator.error(payload.params, error.status, error.xhr.response)))
        )
    ))

const resetAssumptionsSuccessEpic: Epic = (action$) =>
  action$.pipe(
    filterRequestOfType(RESET_ASSUMPTIONS, 'success'),
    map(killAllFloatingPanesActionCreator)
  )

const activateAnchorEpic: Epic = (action$) =>
  action$
    .pipe(byType(ACTIVATE_ANCHOR)).pipe(
      mergeMap(({ payload }) => {
        const actions: Action[] = [
          activateAnchorActionCreator(null),
        ]
        const { to } = payload
        switch (to) {
          case 'future_layeradd':
            actions.push(modifyFloatingPaneActionCreator('future', {
              mode: 'all-layers',
            }) as Action)
            break

          case 'future_layers':
            actions.push(modifyFloatingPaneActionCreator('future', {
              mode: 'member-layers',
            }) as Action)
            break

          case 'future_assumptions':
            actions.push(modifyFloatingPaneActionCreator('future', {
              mode: 'assumptions',
            }) as Action)
            break

          default:
            return empty()
        }

        return of(...actions)
      }))

export const futureEpic = combineEpics(
  getFutureEpic,
  patchLayerEpic,
  postLayerEpic,
  deleteLayerEpic,
  postLayerSuccessEpic,
  patchLayerSuccessEpic,
  deleteLayerSuccessEpic,
  getAssumptionsEpic,
  patchAssumptionsEpic,
  patchAssumptionsSuccessEpic,
  layerCheckEpic,
  useHintsEpic,
  resetAssumptionsEpic,
  resetAssumptionsSuccessEpic,
  activateAnchorEpic,
)
